/**
 * Onboarding API Client Service
 *
 * Calls the Fastify API server's onboarding endpoints (Supabase-backed).
 * The browser never talks to Supabase directly — the API is the single
 * source of truth and uses the Firebase-authenticated user's uid from the
 * session cookie / bearer token, so callers never pass a uid.
 */

import type { IMerchant, IOnboardingApplication } from '@order-up/models';
import { createApiClient } from './apiClient';

const apiClient = createApiClient('/api/v1/onboarding');

export interface OnboardingApplicationResponse {
  success: boolean;
  data: IOnboardingApplication;
}

export const onboardingApi = {
  /**
   * Get (or create) the authenticated user's onboarding application.
   * Idempotent — the server creates a draft on first call and returns the
   * existing one on subsequent calls.
   */
  getMine: (): Promise<OnboardingApplicationResponse> =>
    apiClient.get('/me'),

  /**
   * Update a draft application's data and/or current step.
   * The server merges `applicationData` with the existing record, so partial
   * patches are safe.
   */
  update: (
    id: string,
    data: { applicationData?: Partial<IMerchant>; currentStep?: number },
  ): Promise<OnboardingApplicationResponse> =>
    apiClient.patch(`/${id}`, data),

  /** Advance to the next step (server-side atomic increment). */
  nextStep: (id: string): Promise<OnboardingApplicationResponse> =>
    apiClient.post(`/${id}/next-step`),

  /** Go back one step (server-side atomic decrement, clamped at 0). */
  previousStep: (id: string): Promise<OnboardingApplicationResponse> =>
    apiClient.post(`/${id}/previous-step`),

  /** Jump to a specific step. */
  goToStep: (id: string, step: number): Promise<OnboardingApplicationResponse> =>
    apiClient.post(`/${id}/go-to-step`, { step }),

  /** Validate and submit the application for underwriting review. */
  submit: (id: string): Promise<OnboardingApplicationResponse> =>
    apiClient.post(`/${id}/submit`),
};
